import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  AutoAwesomeOutlined,
  DeleteOutline,
  EditOutlined,
  RefreshOutlined,
  StarOutline,
} from '@mui/icons-material';
import {
  createAiSetting,
  deleteAiSetting,
  getAiSettings,
  setDefaultAiSetting,
  updateAiSetting,
} from '../../../../helpers/api';

const emptyForm = {
  name: '',
  provider: 'ANTHROPIC',
  modelId: '',
  baseUrl: '',
  apiKeyEnvVar: 'ANTHROPIC_API_KEY',
  maxTokens: 16000,
  effort: 'medium',
  description: '',
  isActive: true,
  isDefault: false,
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.status?.desc ||
  error?.response?.data?.result?.status?.desc ||
  error?.message ||
  'The request could not be completed.';

const AiModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteModel, setDeleteModel] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadModels = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getAiSettings();
      setModels(Array.isArray(result) ? result : []);
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const openCreateDialog = () => {
    setEditingModel(null);
    setForm({ ...emptyForm });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (model) => {
    setEditingModel(model);
    setForm({
      ...emptyForm,
      ...model,
      baseUrl: model.baseUrl || '',
      apiKeyEnvVar:
        model.apiKeyEnvVar || (model.provider === 'OPENAI' ? 'OPENAI_API_KEY' : 'ANTHROPIC_API_KEY'),
      effort: model.effort || '',
      description: model.description || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const updateField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'provider') {
        next.apiKeyEnvVar = value === 'OPENAI' ? 'OPENAI_API_KEY' : 'ANTHROPIC_API_KEY';
        if (value === 'OPENAI') next.effort = '';
      }
      return next;
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const maxTokens = Number(form.maxTokens);
    if (!Number.isInteger(maxTokens) || maxTokens < 256 || maxTokens > 128000) {
      setError('Maximum tokens must be a whole number between 256 and 128,000.');
      return;
    }
    if (!/^(ANTHROPIC_API_KEY|OPENAI_API_KEY|AI_[A-Z0-9_]+)$/.test(form.apiKeyEnvVar.trim())) {
      setError('Use ANTHROPIC_API_KEY, OPENAI_API_KEY, or a variable name starting with AI_.');
      return;
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      modelId: form.modelId.trim(),
      baseUrl: form.baseUrl.trim() || null,
      apiKeyEnvVar: form.apiKeyEnvVar.trim() || null,
      maxTokens,
      effort: form.provider === 'ANTHROPIC' ? form.effort || null : null,
      description: form.description.trim() || null,
      ...(editingModel ? { id: editingModel.id } : {}),
    };

    setSaving(true);
    try {
      if (editingModel) {
        await updateAiSetting(payload);
        setSuccess('AI model settings updated.');
      } else {
        await createAiSetting(payload);
        setSuccess('AI model added.');
      }
      setDialogOpen(false);
      await loadModels();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (model) => {
    setBusyId(model.id);
    setError('');
    setSuccess('');
    try {
      await setDefaultAiSetting(model.id);
      setSuccess(`${model.name} is now the default model.`);
      await loadModels();
    } catch (defaultError) {
      setError(getErrorMessage(defaultError));
    } finally {
      setBusyId('');
    }
  };

  const handleToggleActive = async (model, isActive) => {
    setBusyId(model.id);
    setError('');
    setSuccess('');
    try {
      await updateAiSetting({ ...model, isActive });
      setSuccess(`${model.name} ${isActive ? 'activated' : 'deactivated'}.`);
      await loadModels();
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async () => {
    if (!deleteModel) return;
    setBusyId(deleteModel.id);
    setError('');
    setSuccess('');
    try {
      await deleteAiSetting(deleteModel.id);
      setSuccess(`${deleteModel.name} was deleted.`);
      setDeleteModel(null);
      await loadModels();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError));
    } finally {
      setBusyId('');
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeOutlined color="primary" />
              <Typography variant="h6" fontWeight={750}>AI Models</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Configure the providers and models available to AI Reports.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh model list">
              <span>
                <IconButton aria-label="Refresh model list" onClick={loadModels} disabled={loading}>
                  <RefreshOutlined />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddOutlined />} onClick={openCreateDialog}>
              Add model
            </Button>
          </Stack>
        </Stack>

        {error && !dialogOpen && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
          {loading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">Loading AI model configurations...</Typography>
            </Stack>
          ) : models.length === 0 ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 8, px: 2, textAlign: 'center' }}>
              <AutoAwesomeOutlined color="disabled" sx={{ fontSize: 36 }} />
              <Typography fontWeight={650}>No AI models configured</Typography>
              <Typography variant="body2" color="text.secondary">Add a provider model to make it available to AI Reports.</Typography>
              <Button variant="outlined" startIcon={<AddOutlined />} onClick={openCreateDialog}>Add model</Button>
            </Stack>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" aria-label="AI model configurations">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f6f8f9' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>API key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map((model) => (
                    <TableRow key={model.id} hover>
                      <TableCell sx={{ minWidth: 210 }}>
                        <Stack spacing={0.4}>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Typography variant="body2" fontWeight={700}>{model.name}</Typography>
                            {model.isDefault && <Chip label="Default" size="small" color="primary" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{model.modelId}</Typography>
                          {model.description && <Typography variant="caption" color="text.secondary">{model.description}</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>{model.provider}</TableCell>
                      <TableCell>
                        <Stack spacing={0.4}>
                          <Typography variant="body2">{model.apiKeyEnvVar || 'Provider default'}</Typography>
                          <Chip
                            label={model.apiKeyConfigured ? 'Configured on server' : 'Not detected'}
                            size="small"
                            color={model.apiKeyConfigured ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ width: 'fit-content' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(model.isActive)}
                              onChange={(event) => handleToggleActive(model, event.target.checked)}
                              disabled={busyId === model.id}
                              inputProps={{ 'aria-label': `${model.isActive ? 'Deactivate' : 'Activate'} ${model.name}` }}
                            />
                          }
                          label={model.isActive ? 'Active' : 'Inactive'}
                          labelPlacement="end"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={model.isDefault ? 'This is the default model' : 'Make default'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Make ${model.name} the default`}
                              color={model.isDefault ? 'primary' : 'default'}
                              disabled={model.isDefault || !model.isActive || busyId === model.id}
                              onClick={() => handleSetDefault(model)}
                            >
                              {busyId === model.id ? <CircularProgress size={18} /> : <StarOutline fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit model">
                          <IconButton size="small" aria-label={`Edit ${model.name}`} onClick={() => openEditDialog(model)} disabled={busyId === model.id}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={model.isDefault ? 'The default model cannot be deleted' : 'Delete model'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Delete ${model.name}`}
                              color="error"
                              disabled={model.isDefault || busyId === model.id}
                              onClick={() => setDeleteModel(model)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSave}>
          <DialogTitle>{editingModel ? 'Edit AI model' : 'Add AI model'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Configuration name" value={form.name} onChange={updateField('name')} required inputProps={{ maxLength: 100 }} />
                <FormControl required>
                  <InputLabel id="ai-provider-label">Provider</InputLabel>
                  <Select labelId="ai-provider-label" label="Provider" value={form.provider} onChange={updateField('provider')}>
                    <MenuItem value="ANTHROPIC">Anthropic</MenuItem>
                    <MenuItem value="OPENAI">OpenAI compatible</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Provider model ID" value={form.modelId} onChange={updateField('modelId')} required inputProps={{ maxLength: 150 }} placeholder="e.g. claude-sonnet-4-5" />
                <TextField
                  label="API key environment variable"
                  value={form.apiKeyEnvVar}
                  onChange={updateField('apiKeyEnvVar')}
                  helperText="Variable name only. The secret remains configured on the server."
                  inputProps={{ maxLength: 100, autoCapitalize: 'characters' }}
                />
                <TextField label="Base URL override" value={form.baseUrl} onChange={updateField('baseUrl')} placeholder="https://api.example.com/v1" inputProps={{ maxLength: 300 }} />
                <TextField
                  label="Maximum tokens"
                  type="number"
                  value={form.maxTokens}
                  onChange={updateField('maxTokens')}
                  inputProps={{ min: 256, max: 128000, step: 1 }}
                  helperText="Between 256 and 128,000"
                />
                {form.provider === 'ANTHROPIC' && (
                  <FormControl>
                    <InputLabel id="ai-effort-label">Reasoning effort</InputLabel>
                    <Select labelId="ai-effort-label" label="Reasoning effort" value={form.effort} onChange={updateField('effort')}>
                      <MenuItem value="">Provider default</MenuItem>
                      {['low', 'medium', 'high', 'xhigh', 'max'].map((effort) => <MenuItem key={effort} value={effort}>{effort}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <TextField label="Description" value={form.description} onChange={updateField('description')} multiline minRows={2} />
              </Box>
              <Divider />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControlLabel control={<Switch checked={Boolean(form.isActive)} onChange={updateField('isActive')} />} label="Model is active" />
                <FormControlLabel control={<Switch checked={Boolean(form.isDefault)} onChange={updateField('isDefault')} />} label="Make default model" />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || !form.name.trim() || !form.modelId.trim()} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
              {saving ? 'Saving...' : editingModel ? 'Save changes' : 'Add model'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deleteModel)} onClose={() => !busyId && setDeleteModel(null)}>
        <DialogTitle>Delete AI model?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes <strong>{deleteModel?.name}</strong> from the available AI model configurations. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModel(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={Boolean(busyId)} startIcon={busyId ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiModels;
